import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchProducts } from './api';
import { fetchOffers } from './offersApi';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency, getCurrencyDecimals } from './currencyHelpers';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Avatar, Chip, Divider, useTheme, alpha, List, ListItem, ListItemText } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import { isBranchOpen } from './branchUtils';
import config from './config';

const getImageUrls = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';
  if (!imageData) return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.thumbnail || imageData.medium || imageData.large) {
      return {
        thumbnail: imageData.thumbnail 
          ? (imageData.thumbnail.startsWith('http') ? imageData.thumbnail : `${config.SITE_BASE_URL}${imageData.thumbnail}`)
          : fallbackUrl,
        medium: imageData.medium 
          ? (imageData.medium.startsWith('http') ? imageData.medium : `${config.SITE_BASE_URL}${imageData.medium}`)
          : fallbackUrl,
        large: imageData.large 
          ? (imageData.large.startsWith('http') ? imageData.large : `${config.SITE_BASE_URL}${imageData.large}`)
          : fallbackUrl
      };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'string') {
    let fullUrl;
    if (imageData.startsWith('http')) fullUrl = imageData;
    else if (imageData.startsWith('data:image')) fullUrl = imageData;
    else if (imageData.startsWith('/images/')) fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    else if (imageData.startsWith('/')) fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    else fullUrl = imageData;
    return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
  }
  
  return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
};

const OrderHistoryPage = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [offerProducts, setOfferProducts] = useState([]);
  const { selectedBranch } = useCart();
  const branchStatus = selectedBranch ? isBranchOpen(selectedBranch) : { isOpen: true };

  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const decimals = getCurrencyDecimals(selectedCurrency);

  // ✅ Open external product URL helper
  const handleOpenExternalProduct = (prodCode, e) => {
    if (e) e.stopPropagation();
    const url = `https://www.elorayem.com/product/${prodCode}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    async function fetchUserDeliveredOrders() {
      const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
      if (!user || !user.Id) { setOrders([]); return; }
      try {
        const res = await axios.get(`${config.API_BASE_URL}/Orders/user/${user.Id}`);
        const allOrders = Array.isArray(res.data) ? res.data : [];
        setOrders(allOrders.filter(o => { 
          const status = o.OrderStatus || o.status || o.Status; 
          return status === 'Delivered' || status === 'Completed' || status === 'Served'; 
        }));
      } catch { setOrders([]); }
    }
    fetchUserDeliveredOrders();
    
    fetchProducts(selectedBranch?.id, selectedCurrency).then(setProducts);
    
    fetchOffers(selectedBranch?.id, selectedCurrency).then(offers => {
      const allOfferProducts = (offers || []).flatMap(offer => 
        (offer.products || []).map(prod => ({ 
          ProductId: prod.id, 
          Id: `${offer.offerCategoryId}_${prod.id}`, 
          ProductName: prod.productName, 
          Price: prod.price, 
          OldPrice: prod.oldPrice, 
          CurrencyPrices: prod.currencyPrices, 
          OldCurrencyPrices: prod.oldCurrencyPrices, 
          ImageUrl: prod.imageJson, 
          ProductImage: prod.imageJson, 
          Description: prod.description, 
          categoryName: offer.categoryName, 
          discount: offer.discount,
          ProductColors: prod.ProductColors || prod.productColors || [],
          ...prod 
        }))
      );
      setOfferProducts(allOfferProducts);
    });
  }, [selectedBranch, selectedCurrency]);

  return (
    <Box sx={{ 
      bgcolor: alpha(theme.palette.background.paper, 0.9), 
      backdropFilter: 'blur(10px)', 
      borderRadius: '24px', 
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`, 
      p: { xs: 3, md: 4 }, 
      m: '2rem auto', 
      maxWidth: 1000, 
      border: `1px solid ${alpha(theme.palette.custom?.white || '#ffffff', 0.2)}` 
    }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <HistoryIcon sx={{ fontSize: 50, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 800, mb: 2, color: 'primary.main' }}>
          {t('orderHistory.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('orderHistory.subtitle')}
        </Typography>
      </Box>

      {orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <HistoryIcon sx={{ fontSize: 80, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('orderHistory.emptyTitle')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {orders.map(order => {
            let items = order.items || (order.OrderItemsJson ? JSON.parse(order.OrderItemsJson) : []);
            
            const productMap = {};
            [...products, ...offerProducts].forEach(p => {
              productMap[String(p.Id)] = p;
              if (p.ProductCode) productMap[String(p.ProductCode)] = p;
              if (p.ProductId) productMap[String(p.ProductId)] = p;
            });
            
            if (Array.isArray(items) && (products.length > 0 || offerProducts.length > 0)) {
              items = items.map(item => {
                const code = item.ProductCode || item.ProductId || item.id || item.Id;
                const prod = productMap[String(code)];
                
                return { 
                  ...item, 
                  code,
                  name: item.name || item.ProductName || (prod && (prod.Name || prod.ProductName)) || code, 
                  image: item.image || item.ProductImage || item.ImageUrl || (prod && (prod.ImageUrl || prod.ProductImage)) || '', 
                  description: item.Description || item.description || (prod && prod.Description) || '',
                  CurrencyPrices: prod?.CurrencyPrices,
                  selectedColor: item.SelectedColor || item.selectedColor || null
                };
              });
            }

            return (
              <Grid item xs={12} md={6} key={order.orderId || order.OrderId}>
                <Card sx={{ 
                  borderRadius: 2, 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column' 
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        #{order.orderId || order.OrderId}
                      </Typography>
                      <Chip 
                        icon={<CheckCircleIcon />} 
                        label={t('orderHistory.completed')} 
                        color="success" 
                        size="small" 
                        variant="outlined" 
                        sx={{ mt: 1 }} 
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {order.customerName || order.CustomerName || t('common.unknown')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {(() => { 
                          const raw = order.CreatedDate || order.createdDate || order.CreatedAt || order.createdAt || order.OrderDate || order.date; 
                          if (!raw) return ''; 
                          const d = new Date(raw); 
                          if (isNaN(d.getTime())) return raw; 
                          return d.toLocaleString(
                            i18n.language === 'ar' ? 'ar-EG' : 'en-US', 
                            { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }
                          ); 
                        })()}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ mt: 2 }}>
                      {items.slice(0, 3).map((item, idx) => {
                        const rawImageData = item.image || item.ProductImage || item.ImageUrl || item.imageJson || '';
                        const imageUrls = getImageUrls(rawImageData);
                        const displayUrl = imageUrls.thumbnail;
                        const qty = item.quantity || item.Quantity || 1;
                        const displayPrice = getPriceInCurrency(item, selectedCurrency);
                        const selectedColor = item.selectedColor || item.SelectedColor || null;
                        const description = item.description || '';
                        
                        return (
                          <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Avatar 
                                src={displayUrl} 
                                variant="rounded" 
                                sx={{ width: 40, height: 40, mr: 1, border: '1px solid', borderColor: 'divider' }} 
                                imgProps={{ onError: (e) => { e.target.src = '/logo-storesye.png'; } }} 
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                  {item.name}
                                </Typography>
                                
                                {selectedColor && (
                                  <Chip 
                                    label={`🎨 ${selectedColor}`}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ 
                                      height: 18, 
                                      fontSize: '0.65rem', 
                                      fontWeight: 600,
                                      mt: 0.3
                                    }}
                                  />
                                )}
                                
                                {description && (
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      display: 'block',
                                      fontSize: '0.7rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                      mt: 0.3
                                    }}
                                  >
                                    {description}
                                  </Typography>
                                )}
                                
                                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mt: 0.3 }}>
                                  {displayPrice.toFixed(decimals)} {currencySymbol}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="secondary" sx={{ fontWeight: 'bold', minWidth: 28, textAlign: 'center' }}>
                                × {qty}
                              </Typography>
                            </Box>
                            
                            {item.isCustom && Array.isArray(item.SubProducts) && item.SubProducts.length > 0 && (
                              <Box sx={{ mt: 1, ml: 1, width: '100%' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5, fontSize: '0.8rem' }}>
                                  {t('customProduct.selectedSubproducts', 'Selected Options')}:
                                </Typography>
                                <List dense disablePadding>
                                  {item.SubProducts.map((sp, spIdx) => {
                                    if (!sp.Name) return null;
                                    const spPrice = getPriceInCurrency(sp, selectedCurrency);
                                    return (
                                      <ListItem key={sp.Id || spIdx} sx={{ pl: 0, py: 0.3 }}>
                                        <ListItemText 
                                          primary={
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                {sp.Name}
                                              </Typography>
                                              {spPrice > 0 && (
                                                <Typography variant="body2" color="primary" sx={{ fontSize: '0.7rem' }}>
                                                  {t('price', 'Price')}: {spPrice.toFixed(decimals)} {currencySymbol}
                                                </Typography>
                                              )}
                                              {sp.Quantity !== undefined && sp.Quantity !== null && sp.Quantity !== '' && sp.Quantity !== 'null' && sp.Quantity !== 0 && (
                                                <Typography variant="body2" color="secondary" sx={{ fontSize: '0.7rem' }}>
                                                  {t('quantity', 'Qty')}: {sp.Quantity}
                                                </Typography>
                                              )}
                                            </Box>
                                          } 
                                        />
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                      {items.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                          +{items.length - 3} {t('orderHistory.moreItems')}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
                    {items.map((item, idx) => {
                      const prodId = item.ProductCode || item.ProductId || item.id || item.Id || item.code;
                      return (
                        <Button 
                          key={idx}
                          variant="contained" 
                          fullWidth 
                          disabled={!branchStatus.isOpen} 
                          startIcon={!branchStatus.isOpen ? <AccessTimeIcon /> : <OpenInNewIcon />} 
                          onClick={(e) => handleOpenExternalProduct(prodId, e)} 
                          sx={{ 
                            borderRadius: 3, 
                            fontWeight: 'bold', 
                            bgcolor: 'primary.main', 
                            color: 'primary.contrastText', 
                            '&:hover': { bgcolor: 'primary.dark' },
                            textTransform: 'none'
                          }}
                        >
                          {!branchStatus.isOpen ? (t('common.closed') || 'Closed') : `تسوق الان - ${item.name || item.ProductName || ''}`}
                        </Button>
                      );
                    })}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default OrderHistoryPage;
